import {
    Calendar,
    LayoutDashboard,
    MapPin,
    Menu,
    Package,
    Shield,
    Star,
    Trophy,
    User,
    Users,
    X,
    ClipboardList,
    FileText
} from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';

// Alias for consistency
const getAvatarUrl = getImageUrl;

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const baseNavigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Bookings', href: '/admin/bookings', icon: Calendar },
    { name: 'Invoices', href: '/admin/invoices', icon: FileText },
    { name: 'Courts', href: '/admin/courts', icon: MapPin },
    { name: 'Managers', href: '/admin/managers', icon: Users },
    { name: 'Tournaments', href: '/admin/tournaments', icon: Trophy },
    { name: 'Tournament Applications', href: '/admin/tournament-applications', icon: ClipboardList },
  ];

  const superAdminNavigation = [
    { name: 'Reviews', href: '/admin/reviews', icon: Star },
    { name: 'Products', href: '/admin/products', icon: Package },
    { name: 'Super Admin', href: '/admin/super', icon: Shield },
  ];

  const navigation = useMemo(() => {
    console.log('AdminLayout: Computing navigation, user:', user, 'role:', user?.role);
    
    // For manager role, show Invoices and Courts (for creating bookings)
    // Managers cannot access Dashboard or Analytics
    if (user && user.role === 'manager') {
      const managerNav = [
        { name: 'Invoices', href: '/admin/invoices', icon: FileText },
        { name: 'Courts', href: '/admin/courts', icon: MapPin }
      ];
      console.log('AdminLayout: Manager navigation computed:', managerNav);
      return managerNav;
    }
    
    let nav = [...baseNavigation];
    
    if (user && user.role === 'super_admin') {
      nav = [...nav, ...superAdminNavigation];
    }
    
    console.log('AdminLayout: Navigation for role', user?.role, ':', nav);
    return nav;
  }, [user, user?.role]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800 shadow-xl">
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">A</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">Admin</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <nav className="mt-2 px-2 space-y-1">
                {navigation && navigation.length > 0 ? (
                  navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-base font-medium rounded-lg transition-colors ${
                        isActive(item.href)
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="mr-4 h-6 w-6 flex-shrink-0" />
                      {item.name}
                    </Link>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">No menu items available</div>
                )}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
              <Link to="/profile" className="flex-shrink-0 w-full group block" onClick={() => setSidebarOpen(false)}>
                <div className="flex items-center">
                  <div>
                    <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                      {getAvatarUrl(user?.avatar) ? (
                        <img
                          src={getAvatarUrl(user.avatar)}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      )}
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                      {user?.name || 'Admin User'}
                    </p>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                      View profile
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0 lg:fixed lg:inset-y-0 lg:left-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-full border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">A</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">Admin Panel</span>
              </div>
              <nav className="mt-5 flex-1 px-2 space-y-1">
                {navigation && navigation.length > 0 ? (
                  navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive(item.href)
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  ))
                ) : (
                  <div className="px-2 py-2 text-sm text-gray-500">No menu items available</div>
                )}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <Link to="/profile" className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <div>
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                      {getAvatarUrl(user?.avatar) ? (
                        <img
                          src={getAvatarUrl(user.avatar)}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-4 h-4 text-gray-600" />
                      )}
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {user?.name || 'Admin User'}
                    </p>
                    <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                      View profile
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:pl-64 w-full">
        {/* Top bar - Mobile Menu */}
        <div className="sticky top-0 z-40 lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="ml-2 text-lg font-bold text-gray-900 dark:text-white">Admin</span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 w-full overflow-y-auto">
          <div className="min-h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
